import React, { useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogActions, Button, Box, Divider, Typography, FormControlLabel, Checkbox, useMediaQuery } from '@mui/material';
import { Eye, FileText } from 'lucide-react';
import { useUser } from '../../providers/user-provider';
import theme from '../../util/theme/theme';
import { SlideUpTransition } from './mobile/mobile-bottom-sheet';

import InvoiceHeader from './parts/invoice-header';
import InvoiceItems from './parts/invoice-items';
import InvoiceTaxes from './parts/invoice-taxes';
import InvoiceTerms from './parts/invoice-terms';
import EditInvoiceClient from './edit-invoice-client';
import InvoicePreview from './invoice-preview';
import { InvoicePreviewMock, PreviewScaler } from '../invoice-preview-mock';

import useApi from '../../hooks/use-api';
import buildApiRequestConfig from '../../api/url-builder';
import routes from '../../api/routes';
import { useToast } from '../../providers/toaster-provider';
import { useSettings } from '../../providers/settings-provider';
import { useFeatures } from '../../providers/features-provider';
import { defaultInvoiceTerms, defaultQuoteTerms } from '../../pages/invoice-settings';

export interface InvoiceItem {
    id?: string;
    type?: 'item' | 'comment';
    quantity: number;
    description: string;
    unitPrice: number;
    lineTotal: number;
    appliedTaxes?: string[];
    commentHtml?: string;
    taxLabels?: string[];
}

export interface InvoiceTax {
    id: string;
    name: string;
    rate: number;
    amount: number;
}

export interface InvoiceData {
    id?: string;
    status: string;
    invoiceNumber?: string;
    issuedDate: string;
    dueDate?: string;
    clientId?: string;
    companyName: string;
    companyAddress: string;
    companyAddress2: string;
    companySuburb: string;
    companyState: string;
    companyPostalCode: string;
    companyPhone?: string;
    companyFax?: string;
    companyEmail?: string;
    recipientName: string;
    recipientAddress: string;
    invoiceDescriptor?: string;
    items: InvoiceItem[];
    taxes: InvoiceTax[];
    termsAndConditions: string;
    subTotal: number;
    totalTax: number;
    grandTotal: number;
    sendEmail?: boolean;
    provisionDateOffset?: number | null;
    confidenceLevel?: number;
}

interface AddInvoicePopupProps {
    open: boolean;
    nextInvoiceNumber: string;
    termsAndConditions?: string;
    provisionDateOffset?: number;
    onClose: () => void;
    onCreate: (data: InvoiceData) => void;
    isEdit?: { id: string | null, state: boolean };
}

const AddInvoicePopup: React.FC<AddInvoicePopupProps> = ({ open, onClose, onCreate, isEdit, nextInvoiceNumber, provisionDateOffset, termsAndConditions }) => {
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { user } = useUser();
    const { formatDate, companySettings } = useSettings();
    const { showError, showSuccess } = useToast();
    const [invoiceData, setInvoiceData] = useState<InvoiceData>({
        status: '',
        issuedDate: new Date().toISOString().split('T')[0],
        clientId: '',
        companyName: '',
        companyAddress: '',
        companyAddress2: '',
        companySuburb: '',
        companyState: '',
        companyPostalCode: '',
        recipientName: '',
        recipientAddress: '',
        invoiceDescriptor: '',
        items: [
            { id: '1', quantity: 0, description: '', unitPrice: 0, lineTotal: 0, appliedTaxes: [] }
        ],
        taxes: [],
        termsAndConditions: 'This invoice is valid for 7 days. To approve it please write your name and the words - OK',
        subTotal: 0,
        totalTax: 0,
        grandTotal: 0
    });

    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [fetchInvoice, setFetchInvoice] = useApi();
    const [fetchInvoiceSettings, setFetchInvoiceSettings] = useApi();
    const [fetchPaymentFrequency, setFetchPaymentFrequency] = useApi();
    const [paymentBehavior, setPaymentBehavior] = useState<any>(null);
    const [fetchInvoiceStatuses, setFetchInvoiceStatuses] = useApi();
    const [updateStatusRequest, setUpdateStatusRequest] = useApi();
    const [availableTaxes, setAvailableTaxes] = useState<{ id: string; name: string; rate: number; type: 'added' | 'included' }[]>([]);
    const [statusChangeInitiated, setStatusChangeInitiated] = useState(false);
    const [sendEmailOnSaveChecked, setSendEmailOnSave] = useState(true);
    // Invoice emails are gated by the invoice_email_sending feature flag -
    // off (no checkbox, nothing sent) unless it's explicitly enabled for
    // this entity. isVisibleInNav is the strict "flag must be on" check.
    const { isVisibleInNav } = useFeatures();
    const canSendEmail = isVisibleInNav('invoice_email_sending');
    const sendEmailOnSave = canSendEmail && sendEmailOnSaveChecked;
    const [nextStatusName, setNextStatusName] = useState<string>('');
    const [openEditClient, setOpenEditClient] = useState(false);
    const [openPreview, setOpenPreview] = useState(false);
    const [settingsPreview, setSettingsPreview] = useState<{
        headerUrl?: string | null;
        fromAddress?: string;
        fromPhone?: string;
        fromFax?: string;
        fromEmail?: string;
        titlePosition?: 'left' | 'right';
        hideHeaderImage?: boolean;
        showTitle?: boolean;
        showContactInfo?: boolean;
    } | null>(null);
    const [statusLocks, setStatusLocks] = useState({
        isCompanyEditable: true,
        isLineItemEditable: true,
        isStatusEditable: true
    });
    const [createdDate, setCreatedDate] = useState<string>('');
    const [quotedDate, setQuotedDate] = useState<string>('');
    const [acceptedDate, setAcceptedDate] = useState<string>('');
    const [lastEmailDate, setLastEmailDate] = useState<string>('');

    // Helper to calculate line total with taxes included
    const calculateLineTotalWithTaxes = useCallback((quantity: number, unitPrice: number, appliedTaxes: string[] = []) => {
        const baseTotal = quantity * unitPrice;
        const addedTaxes = availableTaxes.filter(tax =>
            tax.type === 'added' && appliedTaxes.includes(tax.id)
        );
        const taxAmount = addedTaxes.reduce((acc, tax) => acc + (baseTotal * tax.rate / 100), 0);
        return baseTotal + taxAmount;
    }, [availableTaxes]);

    const resetForm = () => {
        setInvoiceData({
            status: '',
            issuedDate: new Date().toISOString().split('T')[0],
            clientId: '',
            companyName: '',
            companyAddress: '',
            companyAddress2: '',
            companySuburb: '',
            companyState: '',
            companyPostalCode: '',
            recipientName: '',
            recipientAddress: '',
            items: [
                { id: '1', quantity: 0, description: '', unitPrice: 0, lineTotal: 0, appliedTaxes: [] }
            ],
            taxes: [],
            termsAndConditions: 'This invoice is valid for 7 days. To approve it please write your name and the words - OK',
            subTotal: 0,
            totalTax: 0,
            grandTotal: 0
        });
        setErrors({});
        setPaymentBehavior(null);
        setSendEmailOnSave(true);
    };

    const handleClose = useCallback(() => {
        resetForm();
        onClose();
    }, [onClose]);

    React.useEffect(() => {
        if (isEdit?.state && isEdit.id) {
            const { url, method } = buildApiRequestConfig(routes.invoices.fetchById, [{ param: 'id', value: isEdit.id }]);
            setFetchInvoice({ url, method });
        } else if (!isEdit?.state && open) {
            resetForm();
        }
        // isEdit is a fresh object literal from the parent on every render (not
        // memoized), so depending on it directly would re-run this effect - and
        // call resetForm() - on any unrelated parent re-render while the modal
        // is open (e.g. while the create/update request is in flight), wiping
        // out in-progress edits like an unticked "Send as an Email" checkbox
        // before the user even sees the save complete. Depend on the primitive
        // fields actually used instead.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEdit?.state, isEdit?.id, open, setFetchInvoice]);

    const [fetchTaxes, setFetchTaxes] = useApi();

    React.useEffect(() => {
        if (!user?.entityId) return;
        
        // Fetch invoice statuses for both create and edit modes
        if (open) {
            const { url, method } = buildApiRequestConfig(routes.invoiceSettings.fetchStatusesByEntity, [
                { param: 'entityId', value: user.entityId }
            ]);
            setFetchInvoiceStatuses({ url, method });
        }
    }, [user?.entityId, open, setFetchInvoiceStatuses]);

    // Handle fetched invoice statuses - set default status (create mode) or apply locks (edit mode)
    React.useEffect(() => {
        const { data, isLoading } = fetchInvoiceStatuses;
        if (data && !isLoading) {
            // For create mode: set default status
            if (!isEdit?.state) {
                const defaultStatus = data.find((s: any) => s.isDefault);
                if (defaultStatus && !invoiceData.status) {
                    setStatusChangeInitiated(true);
                    setInvoiceData(prev => ({ ...prev, status: String(defaultStatus.id) }));
                }
            }
            
            // For both modes: when status changes, extract lock configuration from the fetched data
            if (invoiceData.status) {
                const selectedStatus = data.find((s: any) => String(s.id) === invoiceData.status);
                if (selectedStatus) {
                    setStatusLocks({
                        isCompanyEditable: selectedStatus.isCompanyEditable ?? true,
                        isLineItemEditable: selectedStatus.isLineItemEditable ?? true,
                        isStatusEditable: selectedStatus.isStatusEditable ?? true
                    });
                    
                    // Find and set the next status name
                    if (selectedStatus.nextStatusId) {
                        const nextStatus = data.find((s: any) => String(s.id) === String(selectedStatus.nextStatusId));
                        setNextStatusName(nextStatus?.name || '');
                    } else {
                        setNextStatusName('');
                    }
                }
            }
        }
    }, [fetchInvoiceStatuses, isEdit?.state, invoiceData.status]);

    React.useEffect(() => {
        if (!user?.entityId) return;
        const { url, method } = buildApiRequestConfig(routes.taxDefinitions.fetchByEntity, [{ param: 'entityId', value: user.entityId }]);
        setFetchTaxes({ url, method });
    }, [user?.entityId, setFetchTaxes]);

    React.useEffect(() => {
        const { data, isLoading, error } = fetchInvoice;
        if (data && !isLoading) {
            let parsedTaxes: any[] = [];
            
            try {
                if (data.taxBreakdown) {
                    parsedTaxes = JSON.parse(data.taxBreakdown);
                }
            } catch (e) {
                parsedTaxes = [];
            }

            const processedItems = data.invoiceItems?.map((item: any, index: number) => {
                let appliedTaxes: string[] = [];
                if (item.appliedTaxesIds) {
                    if (typeof item.appliedTaxesIds === 'string') {
                        appliedTaxes = item.appliedTaxesIds.split(',')
                            .map((id: string) => id.trim())
                            .filter((id: string) => id !== '');
                    } else if (Array.isArray(item.appliedTaxesIds)) {
                        appliedTaxes = item.appliedTaxesIds.map((id: any) => String(id).trim());
                    }
                } else if (item.appliedTaxes) {
                    appliedTaxes = Array.isArray(item.appliedTaxes) ? item.appliedTaxes.map((id: any) => String(id).trim()) : [];
                }

                return {
                    id: item.id ? String(item.id) : String(index + 1),
                    type: item.type === 'comment' ? 'comment' : 'item',
                    quantity: item.quantity || 0,
                    description: item.description || '',
                    unitPrice: item.unitPrice || 0,
                    lineTotal: (item.quantity || 0) * (item.unitPrice || 0),
                    appliedTaxes,
                    commentHtml: item.commentHtml || '',
                };
            }) || [{ id: '1', quantity: 0, description: '', unitPrice: 0, lineTotal: 0, appliedTaxes: [] }];

            setInvoiceData({
                id: data.id,
                status: String(data.status) || '',
                invoiceNumber: data.invoiceNumber,
                clientId: data.clientId || '',
                companyName: data.clientCompanyName || '',
                companyAddress: data.clientAddress1 || '',
                companyAddress2: data.clientAddress2 || '',
                companySuburb: data.clientSuburb || '',
                companyState: data.clientState || '',
                companyPostalCode: data.clientPostalCode || '',
                companyPhone: data.clientPhone || '',
                companyFax: data.clientFax || '',
                companyEmail: data.clientEmail || '',
                recipientName: data.clientName || '',
                recipientAddress: data.clientAddress || '',
                invoiceDescriptor: data.description || '',
                issuedDate: data.issueDate ? new Date(data.issueDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
                dueDate: data.dueDate ? new Date(data.dueDate).toISOString().split('T')[0] : undefined,
                termsAndConditions: data.termsAndConditions || 'This invoice is valid for 7 days. To approve it please write your name and the words - OK',
                subTotal: data.subtotal || 0,
                totalTax: data.totalTax || 0,
                grandTotal: data.total || 0,
                items: processedItems,
                taxes: parsedTaxes,
                provisionDateOffset: data.provisionDateOffset ?? undefined,
                confidenceLevel: data.confidenceLevel ?? undefined
            });
            // Default to not re-emailing the client on every edit - the user must opt in
            setSendEmailOnSave(false);

            // Extract lifecycle dates and last email info
            if (data._lifecycle) {
                if (data._lifecycle.created) {
                    setCreatedDate(formatDate?.(new Date(data._lifecycle.created)));
                }
                if (data._lifecycle.quoted) {
                    setQuotedDate(formatDate?.(new Date(data._lifecycle.quoted)));
                }
                if (data._lifecycle.accepted) {
                    setAcceptedDate(formatDate?.(new Date(data._lifecycle.accepted)));
                }
                if (data._lifecycle.lastEmail) {
                    setLastEmailDate(formatDate?.(new Date(data._lifecycle.lastEmail)));
                } else {
                    setLastEmailDate('');
                }
            }
        }
        if (error) {
            showError('Error fetching invoice');
        }
    }, [fetchInvoice]);

    React.useEffect(() => {
        const { data, isLoading } = fetchTaxes;
        if (data && !isLoading) {
            const taxes = data.map((tax: any) => ({
                id: String(tax.id),
                name: tax.name,
                rate: tax.rate,
                type: tax.type || 'added'
            }));
            setAvailableTaxes(taxes);

            const addedTaxIds = taxes.filter((t: any) => t.type === 'added').map((t: any) => t.id);

            setInvoiceData(prev => ({
                ...prev,
                items: prev.items.map(item => {
                    if (item.type === 'comment') return item;
                    // In create mode, auto-apply all "added" taxes to items that have none set yet
                    const updatedAppliedTaxes = !isEdit?.state && (!item.appliedTaxes || item.appliedTaxes.length === 0) && addedTaxIds.length > 0
                        ? addedTaxIds
                        : (item.appliedTaxes || []);
                    // Compute lineTotal inline using freshly loaded taxes (avoids stale closure)
                    const base = Number(item.quantity || 0) * Number(item.unitPrice || 0);
                    const addedTaxAmount = taxes
                        .filter((t: any) => t.type === 'added' && updatedAppliedTaxes.includes(t.id))
                        .reduce((acc: number, t: any) => acc + (base * t.rate / 100), 0);
                    return {
                        ...item,
                        appliedTaxes: updatedAppliedTaxes,
                        lineTotal: base + addedTaxAmount
                    };
                })
            }));
        }
    }, [fetchTaxes]);

    // Fetch invoice settings when status changes (for new invoices only)
    React.useEffect(() => {
        if (!user?.entityId || isEdit?.state) return;
        if (!invoiceData.status || !statusChangeInitiated) return;

        const { url, method } = buildApiRequestConfig(routes.invoiceSettings.fetchByEntity, [
            { param: 'entityId', value: user.entityId },
            { param: 'statusId', value: invoiceData.status }
        ]);
        setFetchInvoiceSettings({ url, method });
    }, [invoiceData.status, user?.entityId, isEdit?.state, statusChangeInitiated]);

    // Handle invoice settings response
    React.useEffect(() => {
        const { data, isLoading, error } = fetchInvoiceSettings;
        if (data && !isLoading) {
            // Determine which default terms to use based on status name
            let defaultTerms = defaultInvoiceTerms;
            if (invoiceData.status && fetchInvoiceStatuses.data) {
                const statusObj = fetchInvoiceStatuses.data.find((s: any) => String(s.id) === invoiceData.status);
                if (statusObj?.name?.toLowerCase() === 'quote') {
                    defaultTerms = defaultQuoteTerms;
                }
            }

            setInvoiceData(prev => ({
                ...prev,
                termsAndConditions: data.termsAndConditions || defaultTerms
            }));
            setSettingsPreview({
                headerUrl: data.headerUrl || null,
                fromAddress: data.fromAddress || '',
                fromPhone: data.fromPhone || '',
                fromFax: data.fromFax || '',
                fromEmail: data.fromEmail || '',
                titlePosition: data.titlePosition === 'right' ? 'right' : 'left',
                hideHeaderImage: data.hideHeaderImage === true,
                showTitle: data.showTitle !== false,
                showContactInfo: data.showContactInfo !== false,
            });
            setStatusChangeInitiated(false);
        }
        if (error) {
            setStatusChangeInitiated(false);
        }
    }, [fetchInvoiceSettings, invoiceData.status, fetchInvoiceStatuses.data]);

    const calculateTotals = React.useCallback(() => {
        const lineItems = invoiceData.items.filter(item => item.type !== 'comment');
        const subTotal = lineItems.reduce((sum, item) => {
            const baseAmount = item.quantity * item.unitPrice;
            return sum + baseAmount;
        }, 0);

        const totalTax = availableTaxes
            .filter(tax => tax.type === 'added')
            .reduce((taxSum, tax) => {
                const taxForThisType = lineItems.reduce((itemSum, item) => {
                    if (item.appliedTaxes?.includes(tax.id)) {
                        const baseAmount = item.quantity * item.unitPrice;
                        return itemSum + (baseAmount * tax.rate / 100);
                    }
                    return itemSum;
                }, 0);
                return taxSum + taxForThisType;
            }, 0);

        const grandTotal = subTotal + totalTax;

        setInvoiceData(prev => ({
            ...prev,
            subTotal,
            totalTax,
            grandTotal
        }));
    }, [invoiceData.items, availableTaxes]);

    React.useEffect(() => {
        calculateTotals();
    }, [calculateTotals]);

    const previewTaxes = React.useMemo(() =>
        availableTaxes
            .map(tax => {
                const amount = invoiceData.items.reduce((sum, item) => {
                    if (item.appliedTaxes?.includes(tax.id)) {
                        const base = Number(item.quantity || 0) * Number(item.unitPrice || 0);
                        return sum + (base * tax.rate / 100);
                    }
                    return sum;
                }, 0);
                return { name: `${tax.name} (${tax.rate}%)`, amount };
            })
            .filter(t => t.amount > 0),
        [availableTaxes, invoiceData.items]
    );

    React.useEffect(() => {
        // Clear any previous client's payment behavior immediately - otherwise a client
        // with no analysis yet (or none selected at all) keeps showing the last client's
        // stale average instead of correctly falling back to the entity default.
        setPaymentBehavior(null);
        if (!invoiceData.companyEmail || !user?.entityId) return;
        const { url, method } = buildApiRequestConfig(routes.client.paymentFrequencyByEmail, [
            { param: 'email', value: invoiceData.companyEmail },
            { param: 'entityId', value: user?.entityId }
        ]);
        setFetchPaymentFrequency({ url, method });
    }, [invoiceData.companyEmail, user?.entityId, setFetchPaymentFrequency]);

    React.useEffect(() => {
        const { data, isLoading } = fetchPaymentFrequency;
        if (data && !isLoading) setPaymentBehavior(data);
    }, [fetchPaymentFrequency]);

    const updateInvoiceData = (updates: Partial<InvoiceData>) => {
        setInvoiceData(prev => ({ ...prev, ...updates }));
    };

    const validate = (): boolean => {
        const newErrors: { [key: string]: string } = {};

        if (!invoiceData.companyName) newErrors.companyName = 'Company name is required';
        // if (!invoiceData.recipientName) newErrors.recipientName = 'Recipient name is required';
        // if (!invoiceData.issuedDate) newErrors.issuedDate = 'Issued date is required';
        
        // Validate items (comments are notes, not billable lines - skip line validation for them)
        if (invoiceData.items.filter(item => item.type !== 'comment').length === 0) {
            newErrors.items = 'At least one item is required';
        } else {
            invoiceData.items.forEach((item, index) => {
                if (item.type === 'comment') return;
                if (!item.description) newErrors[`item_${index}_description`] = 'Description is required';
                if (item.quantity <= 0) newErrors[`item_${index}_quantity`] = 'Quantity must be greater than 0';
            });
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = () => {
        if (!validate()) {
            return;
        }

        // Only include taxes that are actually applied to at least one item
        const appliedTaxIds = new Set<string>();
        invoiceData.items.forEach(item => {
            item.appliedTaxes?.forEach(taxId => appliedTaxIds.add(taxId));
        });

        const populatedTaxes: InvoiceTax[] = availableTaxes
            .filter(tax => appliedTaxIds.has(tax.id))
            .map(tax => {
                const totalTaxAmount = invoiceData.items.reduce((sum, item) => {
                    if (item.appliedTaxes?.includes(tax.id)) {
                        const itemSubtotal = item.quantity * item.unitPrice;
                        return sum + (itemSubtotal * tax.rate / 100);
                    }
                    return sum;
                }, 0);

                return {
                    id: tax.id,
                    name: tax.name,
                    rate: tax.rate,
                    amount: totalTaxAmount
                };
            });

        const finalInvoiceData: InvoiceData = {
            ...invoiceData,
            taxes: populatedTaxes,
            sendEmail: sendEmailOnSave,
        };

        onCreate(finalInvoiceData);
    };

    const handleSetToNextStatus = () => {
        if (!invoiceData.id) {
            showError('Invoice ID not found');
            return;
        }

        const currentStatus = fetchInvoiceStatuses.data?.find((s: any) => String(s.id) === invoiceData.status);
        if (!currentStatus || !currentStatus.nextStatusId) {
            showError('No next status available');
            return;
        }

        const { url, method } = buildApiRequestConfig(routes.invoices.update, [{ param: 'id', value: invoiceData.id }]);
        setUpdateStatusRequest({ 
            url, 
            method,
            body: { status: String(currentStatus.nextStatusId) }
        });
    };

    React.useEffect(() => {
        const { data, isLoading, error } = updateStatusRequest;
        if (data && !isLoading) {
            showSuccess('Invoice status updated successfully');
            setInvoiceData(prev => ({ ...prev, status: String(data.status || prev.status) }));
        }
        if (error) {
            showError('Failed to update invoice status');
        }
    }, [updateStatusRequest, showError, showSuccess]);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth={isMobile ? false : "lg"}
            fullWidth
            {...(isMobile ? { TransitionComponent: SlideUpTransition } : {})}
            sx={isMobile ? { '& .MuiDialog-container': { alignItems: 'flex-end' } } : undefined}
            PaperProps={{
                sx: isMobile
                    ? { m: 0, width: '100%', maxWidth: '100%', height: '92vh', maxHeight: '92vh', borderTopLeftRadius: 16, borderTopRightRadius: 16, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }
                    : { borderRadius: 3, minWidth: 600, maxHeight: '90vh' }
            }}
        >
            <DialogContent sx={{ pt: 2, px: isMobile ? 1.5 : 3 }}>
                <InvoiceHeader
                    data={invoiceData}
                    onChange={updateInvoiceData}
                    onStatusChange={(statusId) => {
                        setStatusChangeInitiated(true);
                    }}
                    onEditClientClick={() => setOpenEditClient(true)}
                    entityId={user?.entityId}
                    nextInvoiceNumber={nextInvoiceNumber}
                    isEdit={isEdit?.state || false}
                    createdDate={createdDate}
                    quotedDate={quotedDate}
                    acceptedDate={acceptedDate}
                    lastEmailDate={lastEmailDate}
                    isStatusEditable={statusLocks.isStatusEditable}
                    errors={errors}
                    paymentBehavior={paymentBehavior}
                    provisionDateOffset={provisionDateOffset}
                    isMobile={isMobile}
                />
                <Divider sx={{ my: 2 }} />
                <InvoiceItems
                    items={invoiceData.items}
                    onChange={(items: InvoiceItem[]) => updateInvoiceData({ items })}
                    errors={errors}
                    enabledTaxes={availableTaxes}
                    isLineItemEditable={statusLocks.isLineItemEditable}
                    isEdit={isEdit?.state || false}
                />
                <Box 
                    sx={{ 
                        mt: 3, 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'flex-start' 
                    }}
                >
                    <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 2, width: '100%' }}>
                        <InvoiceTerms
                            terms={invoiceData.termsAndConditions ?? (invoiceData.status === 'quote' ? defaultQuoteTerms : defaultInvoiceTerms)}
                            onChange={(termsAndConditions: string) => updateInvoiceData({ termsAndConditions })}
                        />
                        <InvoiceTaxes
                            subTotal={invoiceData.subTotal}
                            taxes={invoiceData.taxes}
                            grandTotal={invoiceData.grandTotal}
                            onChange={(taxes: InvoiceTax[]) => updateInvoiceData({ taxes })}
                            entityId={user?.entityId}
                            items={invoiceData.items}
                        />
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center', justifyContent: 'space-between', gap: isMobile ? 1 : 0, px: isMobile ? 1.5 : 3, pb: 2 }}>
                <Box display="flex" alignItems="center" gap={1} sx={isMobile ? { justifyContent: 'space-between' } : undefined}>
                    <Button
                        onClick={handleClose}
                        variant="contained"
                        fullWidth={isMobile}
                        sx={{
                            background: 'var(--color-container-background)',
                            color: 'var(--color-text-muted)',
                            boxShadow: 'none',
                            borderRadius: 2,
                            fontWeight: 600,
                            px: 3,
                            '&:hover': { background: 'var(--color-container-background)' }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={() => setOpenPreview(true)}
                        variant="outlined"
                        fullWidth={isMobile}
                        startIcon={isEdit?.state ? <FileText size={16} /> : <Eye size={16} />}
                        sx={{
                            borderColor: 'var(--color-primary)',
                            color: 'var(--color-primary)',
                            boxShadow: 'none',
                            borderRadius: 2,
                            fontWeight: 600,
                            px: 3,
                            whiteSpace: 'nowrap',
                            '&:hover': { borderColor: 'var(--color-primary)', background: 'rgba(var(--color-primary-rgb), 0.0627)' },
                        }}
                    >
                        {isEdit?.state ? 'View Invoice' : 'Preview'}
                    </Button>
                </Box>
                <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} alignItems={isMobile ? 'stretch' : 'center'} gap={1}>
                    { isEdit?.state && nextStatusName && (
                        <Button
                            onClick={handleSetToNextStatus}
                            variant="outlined"
                            fullWidth={isMobile}
                            sx={{
                                boxShadow: 'none',
                                borderRadius: 2,
                                fontWeight: 600,
                                textWrap: 'nowrap',
                                px: 3,
                            }}
                        >
                            Set to {nextStatusName}
                        </Button>
                    )}
                    {canSendEmail && (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={sendEmailOnSave}
                                    onChange={(e) => setSendEmailOnSave(e.target.checked)}
                                    size="small"
                                    sx={{ color: 'var(--color-primary)', '&.Mui-checked': { color: 'var(--color-primary)' }, p: 0.5 }}
                                />
                            }
                            label={<Typography sx={{ fontSize: 13, color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>Send as an Email</Typography>}
                            sx={{ mr: 0, ...(isMobile ? { justifyContent: 'center' } : {}) }}
                        />
                    )}
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        fullWidth={isMobile}
                        sx={{
                            background: 'var(--color-primary)',
                            color: '#fff',
                            boxShadow: 'none',
                            borderRadius: 2,
                            fontWeight: 600,
                            textWrap: 'nowrap',
                            px: 3,
                            '&:hover': { background: 'var(--color-primary)' }
                        }}
                    >
                        {isEdit?.state ? (sendEmailOnSave ? 'Send' : 'Save Changes') : 'Add Invoice'}
                    </Button>
                </Box>
            </DialogActions>

            {/* Invoice Preview (create mode — layout mock) */}
            {!isEdit?.state && (
                <Dialog
                    open={openPreview}
                    onClose={() => setOpenPreview(false)}
                    maxWidth={isMobile ? false : "sm"}
                    fullWidth
                    {...(isMobile ? { TransitionComponent: SlideUpTransition } : {})}
                    sx={isMobile ? { '& .MuiDialog-container': { alignItems: 'flex-end' } } : undefined}
                    PaperProps={{
                        sx: isMobile
                            ? { m: 0, width: '100%', maxWidth: '100%', height: '90vh', maxHeight: '90vh', borderTopLeftRadius: 16, borderTopRightRadius: 16, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }
                            : { borderRadius: 3, maxHeight: '90vh' }
                    }}
                >
                    {/* overflowY: 'scroll' (not MUI's default 'auto') - this
                        dialog's scrollbar visibility feeds back into
                        PreviewScaler's ResizeObserver below: scale is
                        computed from the scaler's own clientWidth, which
                        this scrollbar's presence changes, which changes the
                        scaled content's height, which can toggle whether
                        THIS scrollbar is needed - an infinite loop at
                        whatever width lands the computed height right on
                        that boundary. Reserving the scrollbar track
                        unconditionally removes that dependency. */}
                    <DialogContent sx={{ p: 2, overflowY: 'scroll' }}>
                        <Typography sx={{ fontWeight: 700, fontSize: 16, mb: 1.5, color: 'var(--color-default-text)' }}>Invoice Preview</Typography>
                        <PreviewScaler>
                            <InvoicePreviewMock
                                statusName={fetchInvoiceStatuses.data?.find((s: any) => String(s.id) === invoiceData.status)?.name || 'Invoice'}
                                logoUrl={settingsPreview?.headerUrl ?? null}
                                companyName={companySettings?.company?.name || ''}
                                fromAddress={settingsPreview?.fromAddress}
                                fromPhone={settingsPreview?.fromPhone}
                                fromFax={settingsPreview?.fromFax}
                                fromEmail={settingsPreview?.fromEmail}
                                titlePosition={settingsPreview?.titlePosition}
                                hideHeaderImage={settingsPreview?.hideHeaderImage}
                                showTitle={settingsPreview?.showTitle}
                                showContactInfo={settingsPreview?.showContactInfo}
                                invoiceDate={invoiceData.issuedDate}
                                invoiceNumber={invoiceData.invoiceNumber || nextInvoiceNumber}
                                recipientName={invoiceData.recipientName}
                                clientCompanyName={invoiceData.companyName}
                                clientAddress={invoiceData.companyAddress}
                                clientAddress2={invoiceData.companyAddress2}
                                clientSuburb={invoiceData.companySuburb}
                                clientState={invoiceData.companyState}
                                clientPostalCode={invoiceData.companyPostalCode}
                                clientPhone={invoiceData.companyPhone}
                                clientEmail={invoiceData.companyEmail}
                                items={invoiceData.items.map(item => ({
                                    ...item,
                                    taxLabels: item.type === 'comment' ? [] : availableTaxes
                                        .filter(t => item.appliedTaxes?.includes(t.id))
                                        .map(t => `${t.name} (${t.rate}%)`)
                                }))}
                                taxes={previewTaxes}
                                subtotal={invoiceData.subTotal}
                                total={invoiceData.grandTotal}
                                termsAndConditions={invoiceData.termsAndConditions || ''}
                            />
                        </PreviewScaler>
                    </DialogContent>
                    <DialogActions sx={{ px: 2, pb: 2 }}>
                        <Button
                            onClick={() => setOpenPreview(false)}
                            variant="contained"
                            sx={{ background: 'var(--color-container-background)', color: 'var(--color-text-muted)', boxShadow: 'none', borderRadius: 2, fontWeight: 600, '&:hover': { background: 'var(--color-container-background)' } }}
                        >
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>
            )}

            {/* View Invoice (edit mode — real PDF) */}
            {isEdit?.state && invoiceData.id && (
                <InvoicePreview
                    open={openPreview}
                    invoiceId={invoiceData.id}
                    onClose={() => setOpenPreview(false)}
                />
            )}

            {/* Edit Client Modal */}
            <EditInvoiceClient
                open={openEditClient}
                onClose={() => setOpenEditClient(false)}
                isCompanyEditable={statusLocks.isCompanyEditable}
                isEdit={isEdit?.state || false}
                onSave={(data) => {
                    updateInvoiceData({
                        companyName: data.company,
                        recipientName: data.recipient,
                        companyAddress: data.streetAddress1,
                        companyAddress2: data.streetAddress2 || '',
                        companySuburb: data.suburb,
                        companyState: data.state,
                        companyPostalCode: data.postalCode,
                        companyPhone: data.phone,
                        companyFax: data.fax,
                        companyEmail: data.email
                    });
                    setOpenEditClient(false);
                }}
                initialData={{
                    company: invoiceData.companyName,
                    recipient: invoiceData.recipientName,
                    streetAddress1: invoiceData.companyAddress,
                    streetAddress2: invoiceData.companyAddress2,
                    suburb: invoiceData.companySuburb,
                    state: invoiceData.companyState,
                    postalCode: invoiceData.companyPostalCode,
                    country: '', // You can add country to InvoiceData if needed
                    phone: invoiceData.companyPhone,
                    fax: invoiceData.companyFax,
                    email: invoiceData.companyEmail
                }}
            />
        </Dialog>
    );
};

export default AddInvoicePopup;
